import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import WorkerEventLayout from "../components/WorkerEventLayout";
import Spinner from "../components/Spinner";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CheckInModal from "../components/CheckInModal";
import {
  MagnifyingGlassIcon,
  XCircleIcon,
  StarIcon,
  TagIcon,
  TicketIcon,
  MusicalNoteIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { SERVER_BASE_URL } from "../config";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

// CancelConfirmModal component for confirmation dialog
function CancelConfirmModal({ isOpen, onClose, guest, onConfirm }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Cancel Check-in</h3>
        <p className="mb-4">
          Are you sure you want to cancel check-in for <strong>{guest.name}</strong>?
        </p>
        <div className="flex justify-center space-x-3">
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Cancel Check-in
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// PropTypes for CancelConfirmModal
CancelConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  guest: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

function WorkerGuestListPage() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { id: eventId } = useParams();

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkedInCounts, setCheckedInCounts] = useState({});
  const [eventStartTime, setEventStartTime] = useState(null);

  // Cancellation confirmation states
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [guestToCancel, setGuestToCancel] = useState(null);

  // Via filter states
  const [viaFilter, setViaFilter] = useState(null);
  const [showViaDropdown, setShowViaDropdown] = useState(false);

  // Category filter states
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Refs for clicking outside dropdowns
  const viaDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Функция для определения, мобильное ли устройство
  const isMobile = () => window.innerWidth <= 768;

  // Function to render category icon, similar to GuestListPage
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "VIP":
        return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
      case "BACKSTAGE":
        return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
      case "ARTIST":
        return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
      case "STAFF":
        return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
      case "REGULAR":
      default:
        return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
    }
  };

  // Hook to detect clicks outside of an element
  const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("mousedown", handleClick);
      };
    });
  };

  // Close dropdowns when clicking outside
  useOutsideClick(viaDropdownRef, () => setShowViaDropdown(false));
  useOutsideClick(categoryDropdownRef, () => setShowCategoryDropdown(false));

  // Function to get unique Via values
  const getUniqueViaValues = () => {
    const viaValues = guests
      .map((guest) => guest.type || "N/A")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return viaValues;
  };

  // Function to get unique Category values
  const getUniqueCategoryValues = () => {
    const categoryValues = guests
      .map((guest) => guest.category || "REGULAR")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return categoryValues;
  };

  // Function to clear Via filter
  const clearViaFilter = () => {
    setViaFilter(null);
    setShowViaDropdown(false);
  };

  // Function to clear Category filter
  const clearCategoryFilter = () => {
    setCategoryFilter(null);
    setShowCategoryDropdown(false);
  };

  const isPercentageDiscountAdmission = (admission) => {
    if (typeof admission !== "string") return false;
    const normalizedAdmission = admission.trim().toUpperCase();
    return normalizedAdmission !== "FREE" && normalizedAdmission.includes("%");
  };

  const fetchGuests = useCallback(async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/?event_id=${eventId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to load guest list");
      }
      const data = await response.json();
      setGuests(data);

      const initialCheckedInCounts = {};
      data.forEach((guest) => {
        initialCheckedInCounts[guest.id] = guest.checked_in_count || 0;
      });
      setCheckedInCounts(initialCheckedInCounts);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Failed to load guest list.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchEventDetails = useCallback(async () => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) {
        throw new Error("Failed to load event details");
      }
      const data = await response.json();
      setEventStartTime(new Date(data.start_time));
    } catch (error) {
      console.error("Error fetching event details:", error);
      toast.error("Failed to load event details.");
    }
  }, [eventId]);

  useEffect(() => {
    fetchGuests();
    fetchEventDetails();
  }, [fetchGuests, fetchEventDetails]);

  useEffect(() => {
    const pollingInterval = setInterval(fetchGuests, 30000);
    return () => {
      clearInterval(pollingInterval);
    };
  }, [fetchGuests]);

  // Сортировка
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (criteria) => {
    if (criteria === sortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) {
      return <span className="ml-1 text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▲
      </span>
    ) : (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▼
      </span>
    );
  };

  const sortedGuests = [...guests].sort((a, b) => {
    if (sortBy === "name") {
      return sortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (sortBy === "plus") {
      return sortOrder === "asc" ? a.plus - b.plus : b.plus - a.plus;
    }
    if (sortBy === "via") {
      return sortOrder === "asc"
        ? (a.type || "").localeCompare(b.type || "")
        : (b.type || "").localeCompare(a.type || "");
    }
    if (sortBy === "admission") {
      return sortOrder === "asc"
        ? a.admission.localeCompare(b.admission)
        : b.admission.localeCompare(a.admission);
    }
    if (sortBy === "category") {
      return sortOrder === "asc"
        ? (a.category || "REGULAR").localeCompare(b.category || "REGULAR")
        : (b.category || "REGULAR").localeCompare(a.category || "REGULAR");
    }
    return 0;
  });

  const filteredGuests = sortedGuests.filter((guest) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      guest.name.toLowerCase().includes(query) ||
      (guest.type && guest.type.toLowerCase().includes(query)) ||
      (guest.admission && guest.admission.toLowerCase().includes(query)) ||
      guest.plus.toString().includes(query) ||
      (guest.category && guest.category.toLowerCase().includes(query));

    // Apply Via filter if active
    const matchesViaFilter = !viaFilter || guest.type === viaFilter;

    // Apply Category filter if active
    const matchesCategoryFilter = !categoryFilter || guest.category === categoryFilter;

    return matchesSearch && matchesViaFilter && matchesCategoryFilter;
  });

  const calculateSummary = () => {
    const totalGuests = guests.length;
    const freeGuests = guests.filter(
      (guest) => (guest.admission || "").trim().toUpperCase() === "FREE"
    ).length;
    const discountGuests = guests.filter((guest) =>
      isPercentageDiscountAdmission(guest.admission)
    ).length;
    const totalPluses = guests.reduce((sum, guest) => sum + guest.plus, 0);
    return { totalGuests, freeGuests, discountGuests, totalPluses };
  };

  const summary = calculateSummary();

  const handleConfirmCheckIn = async (numberOfPeople) => {
    if (!selectedGuest) return;
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/${selectedGuest.id}/check-in`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ checked_in_count: numberOfPeople }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update guest check-in status");
      }
      fetchGuests();
      toast.success(`Checked in ${numberOfPeople} for ${selectedGuest.name}.`);
      setShowCheckInModal(false);
      setSelectedGuest(null);
    } catch (error) {
      console.error("Error updating check-in status:", error);
      toast.error("Failed to update check-in status.");
    }
  };

  // Function to handle check-in cancellation - now just opens confirmation modal
  const handleCancelCheckIn = (guest, e) => {
    e.stopPropagation(); // Prevent triggering the row click
    setGuestToCancel(guest);
    setShowCancelConfirmModal(true);
  };

  // Function to actually perform check-in cancellation after confirmation
  const confirmCancelCheckIn = async () => {
    if (!guestToCancel) return;

    try {
      // Calculate the negative value to undo all check-ins
      const cancelValue = -1 * checkedInCounts[guestToCancel.id];

      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/${guestToCancel.id}/check-in`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ checked_in_count: cancelValue }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to cancel guest check-in");
      }

      // Update local state
      setCheckedInCounts((prev) => ({
        ...prev,
        [guestToCancel.id]: 0,
      }));

      fetchGuests();
      toast.success(`Check-in canceled for ${guestToCancel.name}.`);
      setShowCancelConfirmModal(false);
      setGuestToCancel(null);
    } catch (error) {
      console.error("Error canceling check-in:", error);
      toast.error("Failed to cancel check-in.");
    }
  };

  const handleGuestClick = (guest) => {
    // If already checked in, do nothing
    if (isCheckedIn(guest)) {
      return;
    }

    if (!eventStartTime) {
      toast.error("Event start time is not loaded. Please refresh the page.");
      return;
    }
    const now = new Date();
    if (now < eventStartTime) {
      const formattedStartTime = eventStartTime.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      toast.error(
        `Check-in is not allowed before event start time: ${formattedStartTime}`
      );
      return;
    }
    // Если гость ещё не прошёл check-in, открываем модалку
    if (checkedInCounts[guest.id] < guest.plus + 1) {
      setSelectedGuest(guest);
      setShowCheckInModal(true);
    }
  };

  // Проверка, прошел ли гость check-in
  const isCheckedIn = (guest) => {
    return checkedInCounts[guest.id] >= guest.plus + 1;
  };

  if (loading) {
    return (
      <WorkerEventLayout>
        <div className="container mx-auto max-w-5xl px-0 sm:px-4 py-4 flex justify-center items-center">
          <Spinner />
        </div>
      </WorkerEventLayout>
    );
  }

  return (
    <WorkerEventLayout>
      <div
        className="container mx-auto max-w-5xl px-0 sm:px-4 py-4"
        style={{ fontSize: isMobile() ? "0.9rem" : "1rem" }}
      >
        {/* Карточка верхнего блока */}
       <div className="bg-white rounded-lg shadow-md p-2 px-2 sm:p-4 mb-3 mx-0 -mt-1 rounded-t-none">
          {/* Десктопная версия статистики */}
          <div className="hidden sm:grid sm:grid-cols-4 sm:gap-2 mb-4">
            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Total Guests</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{summary.totalGuests}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Free</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{summary.freeGuests}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Discount</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{summary.discountGuests}</span>
              </p>
            </div>

            <div className="py-2 px-3 rounded-lg  bg-indigo-50">
              <p className="text-sm text-gray-600 mb-1">Pluses</p>
              <p className="text-xl font-bold text-gray-800 flex items-center">
                <span>{summary.totalPluses}</span>
              </p>
            </div>
          </div>

          {/* Мобильная версия статистики */}
          <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Total</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {summary.totalGuests}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Free</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {summary.freeGuests}
              </p>
            </div>

            <div className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Discount</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {summary.discountGuests}
              </p>
            </div>

            <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
              <p className="text-[10px] text-gray-600 m-0 text-center">Pluses</p>
              <p className="text-lg font-bold text-gray-800 text-center m-0">
                {summary.totalPluses}
              </p>
            </div>
          </div>

          {/* Filter Section — компактные «чипы» */}
<div className="flex items-center justify-between flex-wrap gap-2">
  {/* Активные фильтры (бейджи) */}
  <div className="flex items-center gap-2 flex-wrap">
    {viaFilter && (
      <div className="inline-flex items-center bg-indigo-50 text-indigo-700 px-2.5 py-1.5 rounded-full border border-indigo-200 text-sm">
        <span className="truncate max-w-[160px]">Via: <strong>{viaFilter}</strong></span>
        <button onClick={clearViaFilter} className="ml-1.5 -mr-1 text-indigo-500 hover:text-indigo-700">×</button>
      </div>
    )}
    {categoryFilter && (
      <div className="inline-flex items-center bg-indigo-50 text-indigo-700 px-2.5 py-1.5 rounded-full border border-indigo-200 text-sm">
        <span className="truncate max-w-[160px]">Category: <strong>{categoryFilter}</strong></span>
        <button onClick={clearCategoryFilter} className="ml-1.5 -mr-1 text-indigo-500 hover:text-indigo-700">×</button>
      </div>
    )}
  </div>

  {/* Группа кнопок-чипов */}
  <div className="flex items-center gap-2">
    {/* Category */}
    <div ref={categoryDropdownRef} className="relative">
      <button
        onClick={() => setShowCategoryDropdown((p) => !p)}
        className={`
          inline-flex items-center gap-2 h-10 px-3.5
          rounded-xl border border-gray-300 bg-white
          shadow-sm hover:bg-gray-50
          text-sm text-gray-700
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0
        `}
      >
        <span className="font-medium">Category</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${showCategoryDropdown ? "rotate-180" : ""}`} />
      </button>

      {showCategoryDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 w-56 max-h-64 overflow-y-auto">
          <ul className="py-1">
            <li
              className="px-3.5 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100"
              onClick={clearCategoryFilter}
            >
              Show All
            </li>
            {getUniqueCategoryValues().map((category) => (
              <li
                key={category}
                className={`px-3.5 py-2 hover:bg-gray-50 cursor-pointer text-sm ${
                  categoryFilter === category ? "bg-indigo-50 text-indigo-700" : ""
                }`}
                onClick={() => {
                  setCategoryFilter(category);
                  setShowCategoryDropdown(false);
                }}
              >
                {category}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    {/* Via */}
    <div ref={viaDropdownRef} className="relative">
      <button
        onClick={() => setShowViaDropdown((p) => !p)}
        className={`
          inline-flex items-center gap-2 h-10 px-3.5
          rounded-xl border border-gray-300 bg-white
          shadow-sm hover:bg-gray-50
          text-sm text-gray-700
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0
        `}
      >
        <span className="font-medium">Via</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${showViaDropdown ? "rotate-180" : ""}`} />
      </button>

      {showViaDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 w-56 max-h-64 overflow-y-auto">
          <ul className="py-1">
            <li
              className="px-3.5 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100"
              onClick={clearViaFilter}
            >
              Show All
            </li>
            {getUniqueViaValues().map((via) => (
              <li
                key={via}
                className={`px-3.5 py-2 hover:bg-gray-50 cursor-pointer text-sm ${
                  viaFilter === via ? "bg-indigo-50 text-indigo-700" : ""
                }`}
                onClick={() => {
                  setViaFilter(via);
                  setShowViaDropdown(false);
                }}
              >
                {via}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
</div>

          {/* === ПОИСК ПОД КНОПКАМИ ФИЛЬТРОВ === */}
            <div className="w-full mt-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>

                {/* ВАЖНО: 16px шрифт на мобилках — убирает авто-зум iOS */}
                <input
                  type="text"
                  className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-[16px] sm:text-sm"
                  placeholder="Search guests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  inputMode="search"
                />

                {searchQuery && (
                  <button
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
        </div>

        {/* Таблица гостей */}
        <div className="relative bg-white rounded-none sm:rounded-lg shadow-md overflow-hidden mx-0">
          {loading ? (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
              <Spinner />
            </div>
          ) : filteredGuests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No guests found for this event.</p>
            </div>
          ) : (
            <>
              {/* Mobile header with sorting */}
              <div className="sm:hidden flex justify-between items-center py-1.5 px-3 border-b border-gray-200 bg-gray-50">
                <div className="flex space-x-4 items-center">
                  <div
                    className="text-sm font-medium cursor-pointer"
                    onClick={() => handleSort("name")}
                  >
                    Name {renderSortIcon("name")}
                  </div>
                  <div
                    className="text-sm font-medium cursor-pointer"
                    onClick={() => handleSort("category")}
                  >
                    Cat {renderSortIcon("category")}
                  </div>
                  <div
                    className="text-sm font-medium cursor-pointer"
                    onClick={() => handleSort("via")}
                  >
                    Via {renderSortIcon("via")}
                  </div>
                </div>
              </div>

              {/* Desktop table header */}
              <div className="hidden sm:grid grid-cols-[2fr_0.5fr_0.7fr_1.5fr_1fr_1fr_0.5fr] gap-1 bg-gray-50 py-1.5 px-2 border-b border-gray-200">
                <div
                  className="text-sm font-medium cursor-pointer flex items-center"
                  onClick={() => handleSort("name")}
                >
                  Name {renderSortIcon("name")}
                </div>
                <div
                  className="text-sm font-medium cursor-pointer flex items-center"
                  onClick={() => handleSort("plus")}
                >
                  Plus {renderSortIcon("plus")}
                </div>
                <div className="text-sm font-medium flex items-center">
                  Used
                </div>
                <div
                  className="text-sm font-medium cursor-pointer flex items-center"
                  onClick={() => handleSort("via")}
                >
                  Via {renderSortIcon("via")}
                </div>
                <div
                  className="text-sm font-medium cursor-pointer flex items-center"
                  onClick={() => handleSort("admission")}
                >
                  Admission {renderSortIcon("admission")}
                </div>
                <div
                  className="text-sm font-medium cursor-pointer flex items-center"
                  onClick={() => handleSort("category")}
                >
                  Category {renderSortIcon("category")}
                </div>
                <div className="text-sm font-medium text-center">
                  Action
                </div>
              </div>

              {/* Guest list items */}
              <div>
                {filteredGuests.map((guest) => {
                  const hasDiscountAdmission = isPercentageDiscountAdmission(guest.admission);

                  return (
                    <div
                      key={guest.id}
                      className={`
                        sm:grid sm:grid-cols-[2fr_0.5fr_0.7fr_1.5fr_1fr_1fr_0.5fr]
                        flex flex-col
                        border-b border-gray-200 hover:bg-gray-50 transition duration-200
                        ${isCheckedIn(guest) ? "bg-green-50" : ""}
                        cursor-pointer
                      `}
                      onClick={() => handleGuestClick(guest)}
                    >
                    {/* Mobile layout */}
                    <div className="sm:hidden w-full py-1 px-3 flex justify-between items-start">
                      <div className="flex-1">
                        <div
                          className={`font-medium leading-tight flex items-center ${
                            isCheckedIn(guest)
                              ? "line-through text-green-700"
                              : "text-gray-900"
                          }`}
                        >
                          {renderCategoryIcon(guest.category)}
                          <span className="ml-1">{guest.name}</span>
                          {isCheckedIn(guest) && (
                            <div className="ml-1 text-xs text-green-600 flex items-center leading-none">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="leading-none">Checked in</span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 leading-tight">
                          Plus: {guest.plus} | Used: {checkedInCounts[guest.id] || 0} | Via:{" "}
                          {guest.type || "N/A"} |{" "}
                          <span className={hasDiscountAdmission ? "text-red-600 font-semibold" : ""}>
                            {guest.admission}
                          </span>{" "}
                          | {guest.category || "REGULAR"}
                        </div>
                      </div>
                      {/* Cancel check-in button for mobile */}
                      {isCheckedIn(guest) && (
                        <button
                          onClick={(e) => handleCancelCheckIn(guest, e)}
                          className="text-gray-500 hover:text-gray-700 ml-2"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    {/* Desktop layout */}
                    <div className="hidden sm:flex items-center py-1 px-2">
                      <div className="leading-tight flex items-center">
                        {renderCategoryIcon(guest.category)}
                        <span
                          className={`ml-1 ${
                            isCheckedIn(guest)
                              ? "line-through text-green-700"
                              : "text-gray-900"
                          }`}
                        >
                          {guest.name}
                        </span>
                        {isCheckedIn(guest) && (
                          <span className="ml-2 inline-flex items-center text-xs text-green-600">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 mr-0.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`hidden sm:flex items-center py-1 px-2 ${
                        isCheckedIn(guest) ? "text-green-700" : "text-gray-700"
                      }`}
                    >
                      {guest.plus}
                    </div>
                    <div
                      className={`hidden sm:flex items-center py-1 px-2 ${
                        isCheckedIn(guest) ? "text-green-700" : "text-gray-700"
                      }`}
                    >
                      {checkedInCounts[guest.id] || 0}
                    </div>
                    <div
                      className={`hidden sm:flex items-center py-1 px-2 ${
                        isCheckedIn(guest) ? "text-green-700" : "text-gray-700"
                      }`}
                    >
                      {guest.type || "N/A"}
                    </div>
                    <div
                      className={`hidden sm:flex items-center py-1 px-2 ${
                        hasDiscountAdmission
                          ? "text-red-600 font-semibold"
                          : isCheckedIn(guest)
                            ? "text-green-700"
                            : "text-gray-700"
                      }`}
                    >
                      {guest.admission}
                    </div>
                    <div
                      className={`hidden sm:flex items-center py-1 px-2 ${
                        isCheckedIn(guest) ? "text-green-700" : "text-gray-700"
                      }`}
                    >
                      {guest.category || "REGULAR"}
                    </div>
                    {/* Cancel check-in button for desktop */}
                    <div className="hidden sm:flex items-center justify-center py-1 px-2">
                      {isCheckedIn(guest) && (
                        <button
                          onClick={(e) => handleCancelCheckIn(guest, e)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Cancel check-in"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Check-in Modal */}
        {showCheckInModal && selectedGuest && (
          <CheckInModal
            isOpen={showCheckInModal}
            onClose={() => {
              setShowCheckInModal(false);
              setSelectedGuest(null);
            }}
            guest={selectedGuest}
            onConfirm={handleConfirmCheckIn}
            checkedInCounts={checkedInCounts}
          />
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelConfirmModal && guestToCancel && (
          <CancelConfirmModal
            isOpen={showCancelConfirmModal}
            onClose={() => {
              setShowCancelConfirmModal(false);
              setGuestToCancel(null);
            }}
            guest={guestToCancel}
            onConfirm={confirmCancelCheckIn}
          />
        )}
      </div>
    </WorkerEventLayout>
  );
}

export default WorkerGuestListPage;
